/**
 * Shell registry — maps a ShellName to the React component that paints it.
 *
 * Each shell is the structural frame of a template — the slots it exposes,
 * the responsive layout, the theme scope. Primitives are arranged INSIDE
 * a shell.
 */

import type { ComponentType, ReactNode } from 'react'
import type { ShellName } from './types'

/**
 * Map of slot name to the pre-rendered React node for that slot.
 * The LayoutRenderer fills this; the shell decides where each slot lands.
 *
 * Slot names not in the map are treated as empty. Shells should be tolerant
 * of unknown slot names (they're ignored). This keeps the renderer simple
 * and lets shells evolve their slot vocabulary.
 */
export type ShellSlotMap = Record<string, ReactNode>

export interface ShellProps {
  slots: ShellSlotMap
}

export type Shell = ComponentType<ShellProps>

const REGISTRY = new Map<ShellName, Shell>()

export function registerShell(name: ShellName, component: Shell): void {
  REGISTRY.set(name, component)
}

export function lookupShell(name: ShellName): Shell | undefined {
  return REGISTRY.get(name)
}
